import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { playSound } from '@/sound/audioSystem';
import { eventBus } from '@/events/eventBus';
import { autoPingChannelFor } from '@/utils/commsHelpers';
import { onBookingConfirmed } from '@/domain/bookingConfirmationFlow';
import type { SimEvent } from '@/events/simEvents';
import type {
  ActivityFeedEntry,
  Booking,
  ConversationEntry,
  Deposit,
  StudioId,
} from '@/domain/types';

function pingComms(
  eventType: 'deposit-paid' | 'booking-created',
  body: string,
  opts: { studioId?: StudioId; customerId?: string },
) {
  const brain = useBrainStore.getState();
  const target = autoPingChannelFor(eventType, opts.studioId, brain.internalComms);
  if (!target) return;
  brain.addAutoPing(target, body, {
    customerRefId: opts.customerId,
    timestamp: useSimulationStore.getState().clockIso,
  });
}

// Connects scripted events to brain mutations.
// Each handler is small and idempotent (firing the same event twice should not double-write).

export function handleSimEvent(evt: SimEvent) {
  switch (evt.type) {
    case 'sarah-missed-call':
      handleSarahMissedCall();
      break;
    case 'sarah-callback-ready':
      handleSarahCallbackReady();
      break;
    case 'sarah-call-answered':
      handleSarahCallAnswered();
      break;
    case 'sarah-stripe-deposit':
      handleSarahStripeDeposit();
      break;
    case 'pat-session-finish':
      handlePatSessionFinish();
      break;
    case 'confirmations-queue-4pm':
      handleConfirmationsQueue();
      break;
    case 'eod-metrics':
      handleEodMetrics();
      break;
    case 'daniel-future-readiness-fires':
      handleDanielFutureReadiness();
      break;
    case 'multi-studio-inbound':
      handleMultiStudioInbound();
      break;
    case 'walk-in-arrives':
      handleWalkIn();
      break;
    case 'brandon-rebook-lands':
      handleBrandonRebookLands();
      break;
    case 'deposit-expires-warning':
      handleDepositExpiresWarning();
      break;
    case 'tyler-sales-resolves':
      handleTylerSalesResolves();
      break;
  }
}

function nowIso() {
  return useSimulationStore.getState().clockIso;
}

function pushActivity(a: Omit<ActivityFeedEntry, 'id' | 'timestamp'>) {
  const entry: ActivityFeedEntry = {
    ...a,
    id: `act-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    timestamp: nowIso(),
  };
  useBrainStore.getState().appendActivity(entry);
}

function handleSarahMissedCall() {
  // L5 (D05-06): the overnight call is a missed call on Sarah's record (saved
  // with the customer), so the Agent's list carries it after a reload — not only
  // a feed line.
  const brain = useBrainStore.getState();
  const at = nowIso();
  const id = `convo-missed-overnight-sarah-holman-${at.slice(0, 10)}`;
  if (brain.customers['sarah-holman'] && !brain.customers['sarah-holman'].conversation.some((e) => e.id === id)) {
    brain.addConversationEntry('sarah-holman', {
      id,
      customerId: 'sarah-holman',
      channel: 'voice',
      direction: 'in',
      body: 'Missed call · overnight · Voicemail: "Hi, just calling about the deposit for Saturday."',
      timestamp: at,
      authorRole: 'customer',
    });
  }
  pushActivity({
    type: 'voicemail',
    customerId: 'sarah-holman',
    studioId: 'coburg',
    body: 'Sarah Holman left a voicemail: "Hi, just calling about the deposit for Saturday."',
  });
  useUIStore.getState().pushNotification({
    intensity: 'high',
    title: 'Overnight voicemail',
    body: 'Sarah Holman. Coburg. Callback flagged.',
    ttlMs: 8000,
  });
}

function handleSarahCallbackReady() {
  useUIStore.getState().pushNotification({
    intensity: 'high',
    title: 'Top of queue',
    body: 'Sarah Holman is ready for callback at Coburg.',
    ttlMs: 6000,
  });
}

function handleSarahCallAnswered() {
  pushActivity({
    type: 'outbound-call',
    customerId: 'sarah-holman',
    studioId: 'coburg',
    body: 'Outbound call connected. Sarah agreed to $400 deposit.',
  });
}

function handleSarahStripeDeposit() {
  const brain = useBrainStore.getState();
  const customer = brain.customers['sarah-holman'];
  if (!customer) return;
  const depositId = 'dep-sarah-saturday';
  const bookingId = 'bk-sarah-saturday';
  const deposit: Deposit = {
    id: depositId,
    customerId: 'sarah-holman',
    bookingId,
    studioId: 'coburg',
    artistId: 'pat',
    amountAud: 400,
    feesAud: 7.3,
    netAud: 392.7,
    status: 'paid',
    channelSent: 'sms',
    stripePaymentLinkId: 'plink_mock_sarah',
    stripeCheckoutSessionId: 'cs_mock_sarah',
    expiresAt: nowIso(),
    createdAt: nowIso(),
    paidAt: nowIso(),
    policy: 'seven-day-rule',
    source: 'agent-initiated-call',
  };
  const booking: Booking = {
    id: bookingId,
    customerId: 'sarah-holman',
    studioId: 'coburg',
    artistId: 'pat',
    pieceId: 'piece-sarah-forearm',
    start: '2026-05-09T11:00:00+10:00',
    durationMin: 240,
    sessionLength: 'half-day',
    status: 'confirmed',
    depositId,
    designBrief: 'Black-and-grey forearm, fine detail. Cherry blossom + script.',
    referencePhotos: [
      'https://images.unsplash.com/photo-1611501275019-9b5cda994e8d?w=600&q=70',
      'https://images.unsplash.com/photo-1565058379802-bbe93b2f703a?w=600&q=70',
    ],
    source: 'agent-initiated-call',
    createdAt: nowIso(),
  };
  brain.upsertDeposit(deposit);
  brain.upsertBooking(booking);
  brain.incStat('deposits');
  brain.incStat('networkRevenueAud', 400);
  pushActivity({
    type: 'deposit-paid',
    customerId: 'sarah-holman',
    studioId: 'coburg',
    artistId: 'pat',
    amount: 400,
    body: 'Stripe deposit $400 from Sarah Holman. Booking created. Pat Saturday 11am.',
  });
  pushActivity({
    type: 'booking-created',
    customerId: 'sarah-holman',
    studioId: 'coburg',
    artistId: 'pat',
    body: 'Sarah Holman x Pat. Saturday 11am at Coburg. Half-day.',
  });
  pingComms('deposit-paid',
    '💰 Sarah Holman deposit landed · Coburg · Pat · $400 half-day · Saturday 11am.',
    { studioId: 'coburg', customerId: 'sarah-holman' });
  playSound('deposit-chime');
  eventBus.publish('stripe-webhook-success', { bookingId, depositId });
  // Booking confirmed — auto-send Sarah's pre-session waiver.
  onBookingConfirmed(bookingId);
}

function handlePatSessionFinish() {
  useBrainStore.getState().incStat('inChairRebookings');
  pushActivity({
    type: 'booking-completed',
    customerId: 'aaron-rebook',
    studioId: 'coburg',
    artistId: 'pat',
    body: 'Pat finished a session. Rebooking flow available.',
  });
}

function handleConfirmationsQueue() {
  useUIStore.getState().pushNotification({
    intensity: 'info',
    title: '23 confirmations queued',
    body: 'Tomorrow\'s confirmations are ready for approval.',
    ttlMs: 7000,
  });
}

function handleEodMetrics() {
  pushActivity({
    type: 'booking-created',
    body: 'End of day: 28 deposits at Coburg. 4 cross-studio routes. 6 missed calls recovered. 1 in-chair rebooking.',
  });
}

function handleDanielFutureReadiness() {
  const brain = useBrainStore.getState();
  const customer = brain.customers['daniel-pham'];
  if (!customer) return;
  pushActivity({
    type: 'future-readiness-fired',
    customerId: 'daniel-pham',
    body: 'May trigger fired for Daniel. Re-contact ready for approval.',
  });
  useUIStore.getState().pushNotification({
    intensity: 'high',
    title: 'Future-readiness trigger',
    body: 'Daniel Pham. May trigger fired (six months on).',
    ttlMs: 8000,
  });
}

function handleMultiStudioInbound() {
  pushActivity({
    type: 'inbound-call',
    customerId: 'maya-chen',
    studioId: 'frankston',
    body: 'Inbound to Frankston. Previously seen at Coburg 8 months ago.',
  });
  useUIStore.getState().addIncomingCall({
    id: `multi-studio-inbound-${Date.now()}`,
    customerId: 'maya-chen',
    studioId: 'frankston',
    startedAt: nowIso(),
  });
  useUIStore.getState().pushNotification({
    intensity: 'high',
    title: 'Inbound to Frankston',
    body: 'Maya Chen. Cross-studio history visible.',
    ttlMs: 8000,
  });
  playSound('inbound-ring');
}

function handleWalkIn() {
  pushActivity({
    type: 'meta-dm',
    studioId: 'coburg',
    body: 'Walk-in at Coburg Front Desk. Captured by apprentice.',
  });
}

/**
 * Demo trigger: Brandon closes an in-chair rebook live. Drops a completed
 * Booking with Outcome A onto his record so the Leaderboard ticks Brandon up
 * by one (+1 last-24h, movement arrow appears) and the Reporting attribution
 * panel sees one more rebook.
 */
function handleBrandonRebookLands() {
  const brain = useBrainStore.getState();
  const now = nowIso();
  const bookingId = `bk-demo-brandon-rebook-${Date.now()}`;
  const startMs = new Date(now).getTime() - 4 * 60 * 60 * 1000;
  const startIso = new Date(startMs).toISOString();
  const newBooking: Booking = {
    id: bookingId,
    customerId: 'aaron-patel',
    studioId: 'coburg',
    artistId: 'brandon',
    start: startIso,
    durationMin: 240,
    sessionLength: 'half-day',
    status: 'completed',
    designBrief: 'Colour realism half-sleeve · session 2 of 4',
    referencePhotos: [],
    source: 'agent-initiated-call',
    createdAt: startIso,
    checkedInAt: startIso,
    checkedOutAt: now,
    amountPaidAud: 800,
    paymentMethod: 'card',
    paidAt: now,
    rebookOutcome: {
      kind: 'rebookedInChair',
      newBookingId: `bk-demo-brandon-rebook-next-${Date.now()}`,
      depositRolledForward: true,
    },
    rebookAttribution: {
      facilitatedBy: 'artist',
      facilitatedByStaffId: 'brandon',
      capturedAt: now,
    },
  };
  brain.upsertBooking(newBooking);
  brain.incStat('inChairRebookings');
  pushActivity({
    type: 'rebook-attributed',
    customerId: 'aaron-patel',
    studioId: 'coburg',
    artistId: 'brandon',
    body: 'Brandon closed an in-chair rebook · deposit rolling forward to next session.',
  });
  pingComms('booking-created',
    '🏆 Brandon just closed an in-chair rebook · leaderboard +1.',
    { studioId: 'coburg', customerId: 'aaron-patel' });
  playSound('deposit-chime');
}

/**
 * Demo trigger: a pending deposit on the floor is about to expire. Sets its
 * `expiresAt` to ~45 minutes from the sim clock so the queue + deposits
 * surface light up "expiring soon".
 */
function handleDepositExpiresWarning() {
  const brain = useBrainStore.getState();
  // Pick the first pending deposit we can find (procedural fixtures generate
  // plenty). If none, fall back gracefully.
  const pending = Object.values(brain.deposits).find((d) => d.status === 'pending');
  if (!pending) {
    pushActivity({
      type: 'deposit-expired',
      body: 'No pending deposits to flag — fixtures look clean.',
    });
    return;
  }
  const soonMs = new Date(nowIso()).getTime() + 45 * 60 * 1000;
  brain.upsertDeposit({
    ...pending,
    expiresAt: new Date(soonMs).toISOString(),
  });
  pushActivity({
    type: 'deposit-link-sent',
    customerId: pending.customerId,
    studioId: pending.studioId,
    body: `Deposit expiring in 45 min · ${pending.customerId}.`,
  });
  useUIStore.getState().pushNotification({
    intensity: 'high',
    title: 'Deposit expiring',
    body: `${pending.customerId} · 45 minutes left to pay.`,
    ttlMs: 7000,
  });
}

/**
 * Demo trigger: Tyler's seeded sales follow-up resolves as "booked". Walks the
 * full Outcome C round-trip — task created → sales picks it up → resolved.
 */
function handleTylerSalesResolves() {
  const brain = useBrainStore.getState();
  brain.resolveRebookFollowup('rfu-tyler-okafor-floor', {
    outcome: 'booked',
    resolvedAt: nowIso(),
  });
  pushActivity({
    type: 'rebook-attributed',
    customerId: 'tyler-okafor',
    studioId: 'coburg',
    body: 'Sales closed Tyler · the deferred rebook just booked.',
  });
  pingComms('booking-created',
    '✅ Sales closed Tyler Okafor · deferred rebook converted.',
    { studioId: 'coburg', customerId: 'tyler-okafor' });
  playSound('deposit-chime');
}

export const _internal = { pushActivity };

type _Unused = ConversationEntry; // type re-export shim to silence linter
const _u: _Unused | null = null;
void _u;
